// Columna de filtros de «Teléfonos» (`.col-filters` del mock): Búsqueda · Elegibilidad por nivel ·
// Marca · Rango de precio · Financiera aceptada.
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { TierPill } from '@/shared/patterns/Pills';
import { NoSource } from '@/shared/patterns/NoSource';
import { Input } from '@/kit';
import { cn } from '@/lib/cn';
import { TIER_LABEL } from '@/shared/patterns/Pills/tierPresentation';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import {
    countActivePhoneFilters,
    EMPTY_PHONE_FILTERS,
    financierChips,
    type PhoneFilters,
} from '../lib/phoneCatalog';
import { PLAN_LEVELS, shortThousands, type LevelPlan } from '../lib/levelPlans';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD = 'h-[26px] text-[11.5px]';
const SELECT =
    'h-[26px] w-full rounded-md border border-input bg-transparent px-2 text-[11.5px] outline-none focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50';

export const PLANS_UNREADABLE =
    'No se pudieron leer los planes de crédito por nivel (loan-offerings `GET /credit-plans`).';

export function PhoneFiltersPanel({
    filters,
    brands,
    financiers,
    plans,
    onChange,
    collapsed,
    onToggleCollapsed,
}: {
    filters: PhoneFilters;
    brands: string[];
    financiers: string[];
    /** `null` si los planes no se pudieron leer: el filtro de nivel se apaga. */
    plans: LevelPlan[] | null;
    onChange: (next: PhoneFilters) => void;
    collapsed: boolean;
    onToggleCollapsed: () => void;
}) {
    const patch = (partial: Partial<PhoneFilters>) => {
        onChange({ ...filters, ...partial });
    };
    const levelCaps = (plans ?? [])
        .map(
            (plan) =>
                `${TIER_LABEL[ClientLevelEnum[plan.level]]} ${shortThousands(plan.maxFinancedCents)}`,
        )
        .join(' · ');

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={countActivePhoneFilters(filters)}
            onReset={() => {
                onChange(EMPTY_PHONE_FILTERS);
            }}
        >
            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <Input
                    type="search"
                    value={filters.query}
                    onChange={(e) => {
                        patch({ query: e.target.value });
                    }}
                    placeholder="SKU, marca o modelo"
                    aria-label="SKU, marca o modelo"
                    className={FIELD}
                />
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Elegibilidad por nivel</div>
                <div className="grid grid-cols-4 gap-1">
                    {PLAN_LEVELS.map((level) => (
                        <TierPill
                            key={level}
                            level={ClientLevelEnum[level]}
                            selected={filters.level === level}
                            disabled={plans === null}
                            title={plans === null ? PLANS_UNREADABLE : undefined}
                            onToggle={() => {
                                patch({ level: filters.level === level ? '' : level });
                            }}
                            className="w-full justify-center"
                        />
                    ))}
                    <button
                        type="button"
                        aria-pressed={filters.level === ''}
                        onClick={() => {
                            patch({ level: '' });
                        }}
                        className={cn(
                            'w-full whitespace-nowrap rounded-full border px-1.5 py-0.5 text-center text-[9.5px] font-semibold transition-colors',
                            filters.level === ''
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-card hover:bg-accent',
                        )}
                    >
                        Todos
                    </button>
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground">
                    {plans === null ? <NoSource reason={PLANS_UNREADABLE} /> : `Máx: ${levelCaps}`}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Marca</div>
                <select
                    value={filters.brand}
                    onChange={(e) => {
                        patch({ brand: e.target.value });
                    }}
                    aria-label="Marca"
                    className={SELECT}
                >
                    <option value="">Todas</option>
                    {brands.map((brand) => (
                        <option key={brand} value={brand}>
                            {brand}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Rango de precio</div>
                <div className="grid grid-cols-2 gap-1">
                    <Input
                        type="number"
                        value={filters.minPrice}
                        onChange={(e) => {
                            patch({ minPrice: e.target.value });
                        }}
                        placeholder="Mín"
                        aria-label="Precio mínimo"
                        className={cn(FIELD, 'tabular-nums')}
                    />
                    <Input
                        type="number"
                        value={filters.maxPrice}
                        onChange={(e) => {
                            patch({ maxPrice: e.target.value });
                        }}
                        placeholder="Máx"
                        aria-label="Precio máximo"
                        className={cn(FIELD, 'tabular-nums')}
                    />
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Financiera aceptada</div>
                <select
                    value={filters.financier}
                    onChange={(e) => {
                        patch({ financier: e.target.value });
                    }}
                    aria-label="Financiera aceptada"
                    className={SELECT}
                >
                    <option value="">Cualquiera</option>
                    {financierChips(financiers).map((chip) => (
                        <option key={chip.code} value={chip.code}>
                            Con {chip.name}
                        </option>
                    ))}
                </select>
            </div>
        </FiltersColumn>
    );
}
